import React, { useEffect, useRef, useState } from "react";

import { Paragraph, Small } from "@fronterahealth/frontera-ui-components";

import { formatTimeDisplay } from "@pages/VideoSessionsV2/components/EmojiComments/useGetTickMarks";
import { EmojiMarker } from "@pages/VideoSessionsV2/types";

type EmojiMarkersProps = {
  emojiMarkers: EmojiMarker[];
  onMarkerClick: (time: number, commentId: string) => void;
  getXPosition: (time: number) => number;
};
export const EmojiMarkers: React.FC<EmojiMarkersProps> = ({ emojiMarkers, onMarkerClick, getXPosition }) => {
  const [hoveredMarkerId, setHoveredMarkerId] = useState<string | null>(null);
  const [horizontalPosition, setHorizontalPosition] = useState("right");
  const tooltipRef = useRef<Record<string, HTMLDivElement | null>>({});
  useEffect(() => {
    if (hoveredMarkerId && tooltipRef.current[hoveredMarkerId]) {
      const tooltip = tooltipRef.current[hoveredMarkerId];
      if (tooltip) {
        const tooltipRectangle = tooltip.getBoundingClientRect();

        // Calculate horizontal position
        const tooltipWidth = tooltip.offsetWidth;
        const spaceLeft = tooltipRectangle.left;
        const spaceRight = window.innerWidth - tooltipRectangle.right;

        let horizontal = "center";
        if (spaceLeft >= tooltipWidth && spaceLeft >= spaceRight) {
          horizontal = "left";
        } else if (spaceRight >= tooltipWidth && spaceRight >= spaceLeft) {
          horizontal = "right";
        }
        setHorizontalPosition(horizontal);
      }
    } else {
      setHorizontalPosition("center");
    }
  }, [hoveredMarkerId]);
  const handleMouseEnter = (marker: EmojiMarker) => {
    setHoveredMarkerId(marker.commentId);
  };

  const handleMouseLeave = () => {
    setHoveredMarkerId(null);
  };

  return (
    <>
      <div className="h-10 sticky top-5 border-b bg-white" /> {/* empty for emoji markers row */}
      {/* Markers */}
      {emojiMarkers.map((m, idx) => {
        const isHovered = hoveredMarkerId === m.commentId;
        return (
          <div
            key={idx}
            className={`absolute transform -translate-x-1 group cursor-pointer  ${isHovered ? "z-10000" : "z-10"} `}
            style={{
              left: getXPosition(m.time),
              top: 45,
            }}
            onMouseEnter={() => handleMouseEnter(m)}
            onMouseLeave={handleMouseLeave}
            onClick={(e) => {
              e.stopPropagation();
              onMarkerClick(m.time, m.commentId);
            }}
          >
            <div className="relative">
              <span className="z-10">{m.emoji}</span>
              {isHovered && (
                <div
                  ref={(el) => {
                    tooltipRef.current[m.commentId] = el;
                  }}
                  className={`absolute bg-white shadow-lg rounded-lg p-3 w-[200px] z-9999 border border-gray-200 cursor-pointer -bottom-3 ${
                    horizontalPosition === "left"
                      ? "right-3"
                      : horizontalPosition === "right"
                        ? "left-5"
                        : "translate-x-[-50%]"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMarkerClick(m.time, m.commentId);
                  }}
                >
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Paragraph colorType="primary" className="text-sm">
                        {m.authorFirstName} {m.authorLastName}
                      </Paragraph>
                      <Small colorType="secondary" className="text-xs">
                        {formatTimeDisplay(m.time)}
                      </Small>
                    </div>

                    <Paragraph colorType="primary" className="text-sm truncate overflow-hidden">
                      {m.content}
                    </Paragraph>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
};
